import { useEffect, useRef, useState } from 'react';
import styles from './PrintableArtwork.module.scss';
import { ReactComponent as CloseIconPressed } from '../../../assets/images/close.svg';
import GridLoader from '../../GridLoader/GridLoader';
import Button2 from '../../Buttons/Button2/Button2';
import { PrintFormat } from '../../../ressources/interfaces/printFormat';
import { post } from '../../../fetchers/fetchers';
import { PLACE_ORDER_URL, SHIPPING_QUOTE_URL } from '../../../ressources/constants/apiRoutes';
import { preventMultiClickSelection } from '../../../utils/selection';
import { useOverlay } from '../../../utils/overlays';
import { COUNTRIES, EMPTY_ADDRESS, REQUIRED_FIELDS, ShippingAddress, ShippingField, countryRules, normalizeShipping, validateShipping } from './shippingAddress';

interface PrintableArtworkProps {
  displayModal: boolean;
  hideModal: Function;
  title: string | null;
  artworkURL: string | null;
  format: PrintFormat | null;
  isLoading: boolean;
  error?: string | null;
  checkoutEnabled?: boolean;
}

const formatPrice = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-CA', {style: 'currency', currency}).format(cents / 100);

// Mirrors build_printable in the PrintableArtwork lambda: inches become percentages of the poster,
// so the preview has the same layout as the print-ready file at any display size
const PosterPreview = ({artworkURL, format} : {artworkURL: string, format: PrintFormat}) => {
  const signatureMask = `url(${format.signature}) center / contain no-repeat`;
  return (
    <div className={styles.Poster} data-testid="PosterPreview"
      style={{aspectRatio: `${format.width} / ${format.height}`, backgroundColor: format.backgroundColor}}>
      <img className={styles.PosterArtwork} src={artworkURL} alt="Artwork"
        style={{width: `${format.widthRatio * 100}%`}}/>
      <div className={styles.PosterSignature}
        style={{
          width: `${format.signatureWidth / format.width * 100}%`,
          aspectRatio: `${format.signatureAspectRatio}`,
          right: `${format.signatureMargin / format.width * 100}%`,
          bottom: `${format.signatureMargin / format.height * 100}%`,
          backgroundColor: format.primaryColor,
          mask: signatureMask,
          WebkitMask: signatureMask,
        }}/>
    </div>
  );
};

type Quote = {isLoading: boolean, shipping: number | null, currency: string | null, error: string | null};
const NO_QUOTE: Quote = {isLoading: false, shipping: null, currency: null, error: null};

type Order = {isPlacing: boolean, reference: string | null, error: string | null};
const NO_ORDER: Order = {isPlacing: false, reference: null, error: null};

const toPrintifyAddress = (address: ShippingAddress) => ({
  first_name: address.firstName, last_name: address.lastName, email: address.email,
  address1: address.line1, address2: address.line2, city: address.city,
  region: address.region, zip: address.postalCode, country: address.country,
});

const PrintableArtwork = (props : PrintableArtworkProps) => {
  const [step, setStep] = useState<'details' | 'shipping' | 'summary' | 'ordered'>('details');
  const [shipping, setShipping] = useState<ShippingAddress>(EMPTY_ADDRESS);
  const [touched, setTouched] = useState<Partial<Record<ShippingField, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [quote, setQuote] = useState<Quote>(NO_QUOTE);
  const quoteRequestId = useRef(0);
  const [order, setOrder] = useState<Order>(NO_ORDER);
  const orderRequestId = useRef(0);
  // Orders per artwork, so an ordered (or ordering) artwork reopens on its order
  const placedOrders = useRef<Record<string, string>>({});
  const placingArtwork = useRef<string | null>(null);
  useOverlay(props.displayModal);
  const checkoutRef = useRef<HTMLDivElement>(null);
  const scrollIdleTimer = useRef<ReturnType<typeof setTimeout>>();

  // Shown only while scrolling, like the page's scrollbar; toggled on the element so scrolling doesn't re-render
  const showScrollbar = () => {
    checkoutRef.current?.classList.remove(styles.ScrollIdle);
    clearTimeout(scrollIdleTimer.current);
    scrollIdleTimer.current = setTimeout(() => checkoutRef.current?.classList.add(styles.ScrollIdle), 1000);
  };
  useEffect(() => () => clearTimeout(scrollIdleTimer.current), []);

  // On open: the order if this artwork was ordered, else the details; clear the opening click's selection
  useEffect(() => {
    if (props.displayModal) {
      const reference = props.artworkURL ? placedOrders.current[props.artworkURL] : undefined;
      if (props.artworkURL && placingArtwork.current === props.artworkURL) {
        setStep('summary');
      } else {
        // An order still being placed for another artwork no longer updates the popup
        orderRequestId.current++;
        setStep(reference ? 'ordered' : 'details');
        setOrder(reference ? {...NO_ORDER, reference} : NO_ORDER);
      }
      window.getSelection()?.removeAllRanges();
    }
  }, [props.displayModal, props.artworkURL]);

  const updateShipping = (field: ShippingField, value: string) =>
    setShipping(prev => field === 'country' ? {...prev, country: value, region: ''} : {...prev, [field]: value});
  const touch = (field: ShippingField) => setTouched(prev => ({...prev, [field]: true}));

  const errors = validateShipping(shipping);
  const fieldError = (field: ShippingField) => (submitted || touched[field]) ? errors[field] : undefined;
  const rules = countryRules(shipping.country);

  const loadQuote = async (address: ShippingAddress) => {
    const requestId = ++quoteRequestId.current;
    setQuote({...NO_QUOTE, isLoading: true});
    try {
      const response = await post<{shipping: number, currency: string}>(SHIPPING_QUOTE_URL, {
        artwork_url: props.artworkURL, country: address.country, region: address.region, zip: address.postalCode,
      });
      if (requestId === quoteRequestId.current) setQuote({...NO_QUOTE, shipping: response.shipping, currency: response.currency});
    } catch (error: any) {
      console.error('Failed to quote shipping:', error);
      const message = error?.response?.data?.error ?? 'Shipping could not be calculated; please try again';
      if (requestId === quoteRequestId.current) setQuote({...NO_QUOTE, error: message});
    }
  };

  // Until Stripe takes payments this only places a test order: it waits in Printify for manual
  // confirmation, so nothing is printed or charged
  const placeOrder = async () => {
    const requestId = orderRequestId.current;
    const artworkURL = props.artworkURL;
    placingArtwork.current = artworkURL;
    setOrder({...NO_ORDER, isPlacing: true});
    try {
      const response = await post<{orderReference: string}>(PLACE_ORDER_URL, {
        artwork_url: artworkURL, address: toPrintifyAddress(shipping),
      });
      // Recorded even if the popup was closed meanwhile, so reopening shows the order
      if (artworkURL) placedOrders.current[artworkURL] = response.orderReference;
      if (requestId !== orderRequestId.current) return;
      setOrder({...NO_ORDER, reference: response.orderReference});
      setStep('ordered');
    } catch (error: any) {
      console.error('Failed to place the order:', error);
      if (requestId !== orderRequestId.current) return;
      setOrder({...NO_ORDER, error: error?.response?.data?.error ?? 'The order could not be placed; please try again'});
    } finally {
      if (placingArtwork.current === artworkURL) placingArtwork.current = null;
    }
  };

  const confirmShipping = () => {
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    const address = normalizeShipping(shipping);
    setShipping(address);
    setStep('summary');
    loadQuote(address);
  };

  const field = (name: ShippingField, label: string, control: (props: {className: string, id: string, 'aria-invalid': boolean, 'aria-required': boolean, 'aria-describedby'?: string, onBlur: () => void}) => JSX.Element) => {
    const error = fieldError(name);
    const id = `shipping-${name}`;
    const required = REQUIRED_FIELDS.includes(name);
    return (
      <div className={styles.Label}>
        <label htmlFor={id}>{label}{required && <span className={styles.Required} aria-hidden="true"> *</span>}</label>
        {control({
          className: error ? `${styles.Input} ${styles.InputInvalid}` : styles.Input,
          id, 'aria-invalid': !!error, 'aria-required': required,
          'aria-describedby': error ? `${id}-error` : undefined,
          onBlur: () => touch(name),
        })}
        {error && <span className={styles.FieldError} id={`${id}-error`}>{error}</span>}
      </div>
    );
  };

  const price = props.format?.price ?? null;
  const currency = props.format?.currency ?? 'USD';

  let isInnerClick : boolean = false;

  return props.displayModal? (
  <div onClick={() => {if(isInnerClick){isInnerClick = false}else{props.hideModal()}}} onMouseDown={preventMultiClickSelection} className={styles.PrintableArtwork} data-testid="PrintableArtwork">
    <div onClick={() => {isInnerClick = true}} className={styles.PrintableArtworkContainer}>
      <div className={styles.Header}>
        <button className={styles.CloseButton} onClick={() => {props.hideModal()}}><CloseIconPressed className={styles.SVGClose}/></button>
      </div>
      <div className={styles.Content}>
        <div className={styles.Preview}>
          {props.isLoading && <GridLoader />}
          {!props.isLoading && props.error && <p className={styles.Error}>{props.error}</p>}
          {!props.isLoading && !props.error && props.artworkURL && props.format && <PosterPreview artworkURL={props.artworkURL} format={props.format}/>}
        </div>
        <div ref={checkoutRef} className={`${styles.Checkout} ${styles.ScrollIdle}`} data-testid="Checkout"
          onScroll={showScrollbar}>
          {step === 'details' && (<>
            <div className={styles.Details}>
              <h2 className={styles.Title}>{props.title}</h2>
              {props.format && <p className={styles.Dimensions}>{props.format.width} × {props.format.height} in</p>}
              {price !== null && <p className={styles.Price}>{formatPrice(price, currency)}</p>}
              <p className={styles.Notice}>The website is currently under construction. Buying artwork will be available soon.</p>
            </div>
            {props.checkoutEnabled && <Button2 action={() => setStep('shipping')}>Next</Button2>}
          </>)}
          {step === 'shipping' && (<>
            <form className={styles.Form} onSubmit={(e) => e.preventDefault()} noValidate>
              <p className={styles.RequiredNote}>* Required</p>
              <div className={styles.Row}>
                {field('firstName', 'First name', (p) => <input {...p} type="text" autoComplete="shipping given-name"
                  value={shipping.firstName} onChange={(e) => updateShipping('firstName', e.target.value)}/>)}
                {field('lastName', 'Last name', (p) => <input {...p} type="text" autoComplete="shipping family-name"
                  value={shipping.lastName} onChange={(e) => updateShipping('lastName', e.target.value)}/>)}
              </div>
              {field('email', 'Email', (p) => <input {...p} type="email" autoComplete="email" placeholder="jane@example.com"
                value={shipping.email} onChange={(e) => updateShipping('email', e.target.value)}/>)}
              {field('country', 'Country', (p) => <select {...p} autoComplete="shipping country"
                value={shipping.country} onChange={(e) => updateShipping('country', e.target.value)}>
                {COUNTRIES.map(country => <option key={country.code} value={country.code}>{country.name}</option>)}
              </select>)}
              {field('line1', 'Address', (p) => <input {...p} type="text" autoComplete="shipping address-line1" placeholder="123 Main Street"
                value={shipping.line1} onChange={(e) => updateShipping('line1', e.target.value)}/>)}
              {field('line2', 'Apartment, suite, etc.', (p) => <input {...p} type="text" autoComplete="shipping address-line2"
                value={shipping.line2} onChange={(e) => updateShipping('line2', e.target.value)}/>)}
              <div className={styles.Row}>
                {field('city', 'City', (p) => <input {...p} type="text" autoComplete="shipping address-level2"
                  value={shipping.city} onChange={(e) => updateShipping('city', e.target.value)}/>)}
                {field('region', rules.regionLabel, (p) => <select {...p} autoComplete="shipping address-level1"
                  value={shipping.region} onChange={(e) => updateShipping('region', e.target.value)}>
                  <option value="" disabled>Select…</option>
                  {rules.regions.map(region => <option key={region.code} value={region.code}>{region.name}</option>)}
                </select>)}
              </div>
              {field('postalCode', rules.postalLabel, (p) => <input {...p} type="text" autoComplete="shipping postal-code" placeholder={rules.postalPlaceholder}
                value={shipping.postalCode} onChange={(e) => updateShipping('postalCode', e.target.value)}/>)}
            </form>
            {submitted && Object.keys(errors).length > 0 && <p className={styles.Error}>Please correct the highlighted fields</p>}
            <div className={styles.Buttons}>
              <Button2 action={() => setStep('details')}>Previous</Button2>
              <Button2 action={confirmShipping}>Next</Button2>
            </div>
          </>)}
          {step === 'summary' && (<>
            <dl className={styles.Summary}>
              <div className={styles.SummaryLine}>
                <dt>Print</dt>
                <dd>{price !== null ? formatPrice(price, currency) : 'Price coming soon'}</dd>
              </div>
              <div className={styles.SummaryLine}>
                <dt>Shipping</dt>
                <dd>{quote.isLoading ? 'Calculating…' : quote.shipping !== null && quote.currency ? formatPrice(quote.shipping, quote.currency) : '—'}</dd>
              </div>
              <div className={`${styles.SummaryLine} ${styles.Total}`}>
                <dt>Total</dt>
                <dd>{price !== null && quote.shipping !== null ? formatPrice(price + quote.shipping, currency) : '—'}</dd>
              </div>
            </dl>
            {quote.error && <p className={styles.Error}>{quote.error}</p>}
            {order.error && <p className={styles.Error}>{order.error}</p>}
            <p className={styles.Notice}>Taxes not included. Test mode: your order is held for manual confirmation, and nothing is charged.</p>
            <div className={styles.Buttons}>
              <Button2 action={() => setStep('shipping')} disabled={order.isPlacing}>Previous</Button2>
              <Button2 action={placeOrder} disabled={price === null || quote.shipping === null || order.isPlacing}>
                {order.isPlacing ? 'Placing…' : 'Buy'}
              </Button2>
            </div>
          </>)}
          {step === 'ordered' && (<>
            <div className={styles.Details}>
              <h2 className={styles.Title}>Test order received</h2>
              <p className={styles.Notice}>
                Your order for {props.title} is held in Printify for manual confirmation; nothing is printed or charged until then.
              </p>
              <p className={styles.Notice}>Reference: <span className={styles.Reference}>{order.reference}</span></p>
            </div>
            <div className={styles.Buttons}>
              <Button2 action={() => props.hideModal()}>Close</Button2>
            </div>
          </>)}
        </div>
      </div>
    </div>
  </div> ) : null
};

export default PrintableArtwork;
